package com.evoapps.evoshift.ui.finance

import com.evoapps.evoshift.ui.theme.AnimatedAmountText
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.LocalTextStyle
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.PlainTooltip
import androidx.compose.material3.Text
import androidx.compose.material3.TooltipAnchorPosition
import androidx.compose.material3.TooltipBox
import androidx.compose.material3.TooltipDefaults
import androidx.compose.material3.rememberTooltipState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.stateDescription
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.explain.Breakdown
import com.evoapps.evoshift.domain.explain.Calculation
import com.evoapps.evoshift.domain.explain.Figure
import com.evoapps.evoshift.domain.explain.PerHour
import com.evoapps.evoshift.domain.explain.Ratio
import com.evoapps.evoshift.domain.explain.Side
import com.evoapps.evoshift.domain.explain.Sign
import com.evoapps.evoshift.domain.explain.Term
import com.evoapps.evoshift.domain.explain.TermKind
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.ui.user.contractLabel
import com.evoapps.evoshift.ui.util.formatHintHours
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.DialogWidth
import com.evoapps.evoshift.widgets.EvoDialog
import evoshift.composeapp.generated.resources.*
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

/*
 * SH-152: "How was this calculated?" Any financial figure can be wrapped in [ExplainableAmount]:
 * tapped (clicked; on a desktop or the web a hover says so), it opens the [CalculationSheet] with
 * the formula in words and the real numbers, worked out by the domain (domain/explain) from the
 * same values as the figure, so its terms add up to it. Screen readers hear "Calculation
 * available" and the click is labelled "Show how this was calculated". SH-228: every such figure
 * carries the same dotted underline ([explainUnderline]).
 */

/** People's names by user id, for the calculations' person terms (the screen that knows them provides them). */
val LocalExplainNames = staticCompositionLocalOf<Map<String, String>> { emptyMap() }

/** Test tags of the calculation sheet. */
object ExplainTags {
    /** On every explainable figure. */
    const val FIGURE = "explainable_figure"
    const val SHEET_VALUE = "calculation_value"
    const val TERM = "calculation_term"
}

/**
 * [text] (a figure on screen) that opens its [calculation] when tapped; plain text without one.
 * A dotted underline marks it, a hover says what a click does.
 */
@Composable
fun ExplainableAmount(
    text: String,
    calculation: Calculation?,
    currency: String,
    modifier: Modifier = Modifier,
    style: TextStyle = LocalTextStyle.current,
    color: Color = Color.Unspecified,
    fontWeight: FontWeight? = null,
    fontStyle: FontStyle? = null,
    textAlign: TextAlign? = null,
    maxLines: Int = Int.MAX_VALUE,
    overflow: TextOverflow = TextOverflow.Clip,
    animated: Boolean = false
) = ExplainableAmount(
    text, explain = calculation?.let { c -> { c } }, currency = currency, modifier = modifier, style = style, color = color,
    fontWeight = fontWeight, fontStyle = fontStyle, textAlign = textAlign, maxLines = maxLines, overflow = overflow, animated = animated
)

/** [ExplainableAmount] whose calculation is worked out only when it is opened ([explain]; null: not explainable). */
@Composable
fun ExplainableAmount(
    text: String,
    explain: (() -> Calculation?)?,
    currency: String,
    modifier: Modifier = Modifier,
    style: TextStyle = LocalTextStyle.current,
    color: Color = Color.Unspecified,
    fontWeight: FontWeight? = null,
    fontStyle: FontStyle? = null,
    textAlign: TextAlign? = null,
    maxLines: Int = Int.MAX_VALUE,
    overflow: TextOverflow = TextOverflow.Clip,
    /** SH-158: a new value rolls in ([AnimatedAmountText]) instead of popping. */
    animated: Boolean = false
) {
    if (explain == null) {
        if (animated) {
            AnimatedAmountText(
                text, modifier, style = style, color = color, fontWeight = fontWeight, fontStyle = fontStyle,
                textAlign = textAlign, maxLines = maxLines, overflow = overflow
            )
        } else {
            Text(text, modifier, color = color, style = style, fontWeight = fontWeight, fontStyle = fontStyle, textAlign = textAlign, maxLines = maxLines, overflow = overflow)
        }
        return
    }
    Explainable(explain, currency, modifier, underline = false) {
        val lineColor = (if (color != Color.Unspecified) color else style.color.takeIf { it != Color.Unspecified } ?: LocalTextStyle.current.color)
        val underline = Modifier.explainUnderline(lineColor)
        if (animated) {
            AnimatedAmountText(
                text, style = style, color = color, fontWeight = fontWeight, fontStyle = fontStyle,
                textAlign = textAlign, maxLines = maxLines, overflow = overflow, textModifier = underline
            )
            return@Explainable
        }
        Text(
            text,
            underline,
            color = color,
            style = style,
            fontWeight = fontWeight,
            fontStyle = fontStyle,
            textAlign = textAlign,
            maxLines = maxLines,
            overflow = overflow
        )
    }
}

/**
 * SH-228: the one mark of a figure that explains itself (owner, 2026-10-23: "nothing shows they
 * are tappable"): a dotted underline in the text's colour, strong enough to see on the cards and
 * on the navy panels, drawn under the text without changing its size. Every explainable figure
 * gets it from here ([ExplainableAmount] and [Explainable]), so all screens look alike.
 */
fun Modifier.explainUnderline(color: Color): Modifier = drawBehind {
    val y = size.height - 0.5.dp.toPx()
    drawLine(
        color = color.copy(alpha = EXPLAIN_UNDERLINE_ALPHA),
        start = Offset(0f, y),
        end = Offset(size.width, y),
        strokeWidth = 1.5.dp.toPx(),
        // Round dots 3dp apart: a dotted line, the only mark of a tappable explanation (no icons).
        cap = StrokeCap.Round,
        pathEffect = PathEffect.dashPathEffect(floatArrayOf(0.01f, 3.dp.toPx()))
    )
}

/** How strongly the underline shows against the figure's own colour (0.45 was too faint to notice). */
const val EXPLAIN_UNDERLINE_ALPHA = 0.8f

/** [content] that opens [calculation] when tapped (a hover hint on desktop and the web); just [content] without one. */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun Explainable(calculation: Calculation?, currency: String, modifier: Modifier = Modifier, underline: Boolean = true, content: @Composable () -> Unit) =
    Explainable(explain = calculation?.let { c -> { c } }, currency = currency, modifier = modifier, underline = underline, content = content)

/** [Explainable] whose calculation is worked out only when it is opened. */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun Explainable(
    explain: (() -> Calculation?)?,
    currency: String,
    modifier: Modifier = Modifier,
    /** SH-228: the dotted underline under [content] (off where the content draws its own, as [ExplainableAmount]). */
    underline: Boolean = true,
    content: @Composable () -> Unit
) {
    if (explain == null) {
        Box(modifier) { content() }
        return
    }
    var open by rememberSaveable { mutableStateOf(false) }
    val show = stringResource(Res.string.explain_show)
    // SH-228: what a tap does, for screen readers ("Pokaż, jak to policzono").
    val action = stringResource(Res.string.explain_show_how)
    val available = stringResource(Res.string.explain_available)
    val lineColor = LocalContentColor.current
    TooltipBox(
        positionProvider = TooltipDefaults.rememberTooltipPositionProvider(TooltipAnchorPosition.Above),
        tooltip = { PlainTooltip { Text(show) } },
        state = rememberTooltipState(),
        modifier = modifier
    ) {
        Box(
            Modifier
                .handCursor()
                .clickable(onClickLabel = action, role = Role.Button) { open = true }
                .semantics { stateDescription = available }
                .then(if (underline) Modifier.explainUnderline(lineColor) else Modifier)
                .testTag(ExplainTags.FIGURE)
        ) { content() }
    }
    if (open) {
        val calculation = remember(explain) { explain() }
        if (calculation != null) CalculationSheet(calculation, currency, onDismiss = { open = false }) else androidx.compose.runtime.LaunchedEffect(Unit) { open = false }
    }
}

/** The calculation behind a figure: the formula in words, then each term with its numbers, and the result. */
@Composable
fun CalculationSheet(calculation: Calculation, currency: String, onDismiss: () -> Unit) {
    EvoDialog(
        title = stringResource(figureTitle(calculation.figure)),
        subtitle = calculation.side?.let { stringResource(sideLabel(it)) },
        onDismiss = onDismiss,
        dismiss = DialogAction(stringResource(Res.string.close), onDismiss),
        width = DialogWidth.Regular,
        bodyArrangement = Arrangement.spacedBy(10.dp)
    ) {
        Text(
            formatValue(calculation, currency),
            style = MaterialTheme.typography.headlineSmall,
            fontWeight = FontWeight.Bold,
            color = LightGold,
            modifier = Modifier.testTag(ExplainTags.SHEET_VALUE)
        )
        formulaOf(calculation.figure)?.let {
            Text(stringResource(it), style = MaterialTheme.typography.bodySmall, color = Color.White.copy(alpha = 0.75f))
        }
        when (calculation) {
            is Breakdown -> TermList(calculation, currency)
            is Ratio -> {
                Section(calculation.numerator, currency)
                Section(calculation.denominator, currency)
                calculation.value?.let { value ->
                    Text(
                        stringResource(
                            Res.string.explain_ratio,
                            formatMoney(calculation.numerator.value, currency),
                            formatMoney(calculation.denominator.value, currency),
                            formatPercent(value)
                        ),
                        fontWeight = FontWeight.Bold,
                        color = LightGold
                    )
                }
            }
            is PerHour -> PerHourSection(calculation, currency)
        }
        Text(stringResource(Res.string.explain_note), style = MaterialTheme.typography.bodySmall, color = Color.White.copy(alpha = 0.6f))
    }
}

/** SH-191: an SPMH: the net sales with their terms, the hours, and the division. */
@Composable
private fun PerHourSection(calculation: PerHour, currency: String) {
    Section(calculation.sales, currency)
    HorizontalDivider(color = Color.White.copy(alpha = 0.15f))
    val hours = stringResource(Res.string.explain_hours, formatHintHours(calculation.hours))
    AmountRow(
        stringResource(if (calculation.plannedHours) Res.string.explain_spmh_hours_planned else Res.string.explain_spmh_hours_worked),
        hours,
        detail = if (calculation.plannedHours && calculation.side == Side.ACTUAL) stringResource(Res.string.explain_spmh_planned_note) else null,
        bold = true
    )
    calculation.value?.let { value ->
        Text(
            stringResource(
                Res.string.explain_per_hour,
                formatMoney(calculation.sales.value, currency),
                formatHintHours(calculation.hours),
                formatSpmhOrDash(value, currency)
            ),
            fontWeight = FontWeight.Bold,
            color = LightGold
        )
    }
}

/** One part of a share (its labour cost, its income) with its own terms and total. */
@Composable
private fun Section(breakdown: Breakdown, currency: String) {
    HorizontalDivider(color = Color.White.copy(alpha = 0.15f))
    Text(stringResource(figureTitle(breakdown.figure)), fontWeight = FontWeight.Bold, color = Color.White)
    TermList(breakdown, currency)
}

@Composable
private fun TermList(breakdown: Breakdown, currency: String) {
    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
        if (breakdown.terms.isEmpty()) {
            Text(stringResource(Res.string.explain_empty), style = MaterialTheme.typography.bodySmall, color = Color.White.copy(alpha = 0.7f))
        }
        breakdown.terms.forEach { TermRow(it, currency, depth = 0) }
        if (breakdown.terms.size > 1) {
            HorizontalDivider(color = Color.White.copy(alpha = 0.15f))
            AmountRow(stringResource(Res.string.explain_total), formatMoney(breakdown.value, currency), detail = null, bold = true)
        }
    }
}

/** How deep the parts of parts go: deeper ones are in their own calculation on screen. */
private const val MAX_DEPTH = 2

@Composable
private fun TermRow(term: Term, currency: String, depth: Int) {
    val amount = when (term.sign) {
        Sign.NOT_COUNTED -> stringResource(Res.string.explain_not_counted)
        Sign.MINUS -> "− " + formatMoney(term.amount, currency)
        Sign.PLUS -> formatMoney(term.amount, currency)
    }
    Column(Modifier.padding(start = (depth * 16).dp).testTag(ExplainTags.TERM)) {
        AmountRow(termLabel(term), amount, termDetail(term, currency), bold = depth == 0 && term.parts.isNotEmpty(), muted = !term.isCounted)
        if (depth < MAX_DEPTH && term.parts.size > 1 || (depth < MAX_DEPTH && term.parts.size == 1 && term.parts.single().kind != term.kind)) {
            Column(Modifier.padding(top = 4.dp), verticalArrangement = Arrangement.spacedBy(4.dp)) {
                term.parts.forEach { TermRow(it, currency, depth + 1) }
            }
        }
    }
}

@Composable
private fun AmountRow(label: String, amount: String, detail: String?, bold: Boolean = false, muted: Boolean = false) {
    val alpha = if (muted) 0.6f else 1f
    Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.Top, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
        Column(Modifier.weight(1f)) {
            Text(
                label,
                fontWeight = if (bold) FontWeight.Bold else FontWeight.Normal,
                fontStyle = if (muted) FontStyle.Italic else FontStyle.Normal,
                color = Color.White.copy(alpha = alpha)
            )
            detail?.let { Text(it, style = MaterialTheme.typography.bodySmall, color = Color.White.copy(alpha = 0.65f)) }
        }
        Text(
            amount,
            fontWeight = if (bold) FontWeight.Bold else FontWeight.Normal,
            fontStyle = if (muted) FontStyle.Italic else FontStyle.Normal,
            color = Color.White.copy(alpha = alpha),
            textAlign = TextAlign.End
        )
    }
}

/** The figure as on screen: a share in percent, an amount in money. */
@Composable
fun formatValue(calculation: Calculation, currency: String): String = when (calculation) {
    is Ratio -> calculation.value?.let { formatPercent(it) } ?: "-"
    is Breakdown -> formatMoney(calculation.value, currency)
    is PerHour -> formatSpmhOrDash(calculation.value, currency)
}

@Composable
private fun personName(id: String?): String =
    id?.let { LocalExplainNames.current[it] } ?: stringResource(Res.string.explain_someone)

@Composable
private fun termLabel(term: Term): String = when (term.kind) {
    TermKind.PERSON -> personName(term.personId)
    TermKind.PERSON_WITHOUT_PAY ->
        if (term.personId != null) stringResource(Res.string.explain_person_without_pay, personName(term.personId))
        else stringResource(Res.string.explain_term_without_pay_shifts)
    TermKind.PERSON_UNPAID -> stringResource(Res.string.explain_person_unpaid, personName(term.personId))
    TermKind.PERSON_NOT_COUNTED -> stringResource(Res.string.explain_person_not_counted, personName(term.personId))
    TermKind.CONTRACT_TYPE -> term.contract?.let { contractLabel(it) } ?: stringResource(Res.string.explain_figure_labour_cost)
    TermKind.LEDGER_ITEM -> term.name.orEmpty()
    TermKind.EMPLOYEE_LINE -> stringResource(Res.string.explain_employee_line, term.payLine?.let { lineName(it) }.orEmpty())
    TermKind.EMPLOYER_LINE -> stringResource(Res.string.explain_employer_line, term.payLine?.let { lineName(it) }.orEmpty())
    else -> stringResource(termLabelRes(term.kind))
}

private fun termLabelRes(kind: TermKind): StringResource = when (kind) {
    TermKind.PERSON, TermKind.PERSON_WITHOUT_PAY -> Res.string.explain_term_without_pay_shifts
    TermKind.PERSON_UNPAID, TermKind.PERSON_NOT_COUNTED, TermKind.LABOUR_COST, TermKind.CONTRACT_TYPE -> Res.string.explain_figure_labour_cost
    TermKind.OPEN_SHIFTS -> Res.string.explain_term_open_shifts
    TermKind.OPEN_SHIFTS_NOT_PRICED -> Res.string.explain_term_open_shifts_not_priced
    TermKind.COMMISSION -> Res.string.explain_term_commission
    TermKind.NET_SALES_KEPT -> Res.string.explain_term_net_sales_kept
    TermKind.SALES_INCOME_MIXED -> Res.string.explain_term_sales_income_mixed
    TermKind.FIXED_REVENUE -> Res.string.explain_figure_fixed_revenue
    TermKind.INCOME -> Res.string.explain_figure_income
    TermKind.RECURRING_REVENUE -> Res.string.explain_figure_recurring_revenue
    TermKind.ONE_OFF_REVENUE -> Res.string.explain_figure_one_off_revenue
    TermKind.REVENUE_ITEMS -> Res.string.explain_term_revenue_items
    TermKind.LEDGER_ITEM -> Res.string.explain_figure_other_costs
    TermKind.TAKINGS -> Res.string.explain_figure_takings
    TermKind.TAKINGS_TYPED -> Res.string.explain_term_takings_typed
    TermKind.TAKINGS_FILE -> Res.string.explain_term_takings_file
    TermKind.TAKINGS_TILL -> Res.string.explain_term_takings_till
    TermKind.SALES_TARGETS, TermKind.SALES_TARGET -> Res.string.explain_figure_sales_target
    TermKind.FIXED_COSTS -> Res.string.explain_figure_fixed_costs
    TermKind.ONE_OFF_COSTS -> Res.string.explain_figure_one_off_costs
    TermKind.SHARED_COSTS -> Res.string.explain_figure_shared_costs
    TermKind.OTHER_COSTS -> Res.string.explain_term_other_costs
    TermKind.REVENUE -> Res.string.explain_figure_revenue
    TermKind.COSTS -> Res.string.explain_figure_costs
    TermKind.BASE_PAY -> Res.string.explain_term_base_pay
    TermKind.OVERTIME_PAY -> Res.string.explain_term_overtime_pay
    TermKind.OVERTIME_50 -> Res.string.explain_term_overtime_50
    TermKind.OVERTIME_100 -> Res.string.explain_term_overtime_100
    TermKind.SUNDAY_HOLIDAY -> Res.string.explain_term_sunday_holiday
    TermKind.MINIMUM_WAGE_TOP_UP -> Res.string.explain_term_minimum_wage_top_up
    TermKind.SICK_PAY -> Res.string.explain_term_sick_pay
    TermKind.SICK_BENEFIT -> Res.string.explain_term_sick_benefit
    TermKind.HOLIDAY_PAY -> Res.string.explain_term_holiday_pay
    TermKind.SALARY_REDUCTION -> Res.string.explain_term_salary_reduction
    TermKind.RATE_CHANGE -> Res.string.explain_term_rate_change
    TermKind.BONUSES -> Res.string.explain_term_bonuses
    TermKind.GROSS -> Res.string.explain_figure_gross
    TermKind.EMPLOYEE_LINE, TermKind.EMPLOYER_LINE -> Res.string.explain_term_employer_contributions
    TermKind.PAID_BY_ZUS -> Res.string.explain_term_paid_by_zus
    TermKind.DEDUCTIONS -> Res.string.explain_term_deductions
    TermKind.EXPENSE_REFUNDS -> Res.string.explain_term_expense_refunds
    TermKind.SHIFTS_EMPLOYER_COST -> Res.string.explain_figure_shifts_employer_cost
    TermKind.MONTH_ITEMS -> Res.string.explain_figure_month_items
    TermKind.TAXES_AND_CONTRIBUTIONS -> Res.string.explain_term_taxes_and_contributions
    TermKind.SUPPLEMENTS_CHANGE -> Res.string.explain_term_supplements_change
    TermKind.SHIFT_SNAPSHOT -> Res.string.explain_term_shift_snapshot
    TermKind.SHIFT_GROSS -> Res.string.explain_term_shift_gross
    TermKind.NIGHT_ALLOWANCE -> Res.string.explain_term_night_allowance
    TermKind.EMPLOYER_CONTRIBUTIONS -> Res.string.explain_term_employer_contributions
    TermKind.HOURS_AT_OWN_RATE -> Res.string.explain_term_hours_at_own_rate
    TermKind.HOURS_AT_MONTH_AVERAGE -> Res.string.explain_term_hours_at_month_average
    TermKind.HOURS_AT_TEAM_AVERAGE -> Res.string.explain_term_hours_at_team_average
    TermKind.PLANNED_SHIFTS -> Res.string.explain_term_planned_shifts
    TermKind.EXPECTED_SALES -> Res.string.explain_term_expected_sales
    TermKind.ROUNDING -> Res.string.explain_term_rounding
    TermKind.SAVED_EARLIER -> Res.string.explain_term_saved_earlier
}

/** What a term is made of: "25,2% × 86 000,00 zł", "8 h × 36,50 zł/h", "Shifts: 3 · 24 h", "Days: 13 of 31". */
@Composable
private fun termDetail(term: Term, currency: String): String? {
    val pieces = buildList {
        val percent = term.percent
        val base = term.base
        val hours = term.hours
        if (percent != null && base != null) add(stringResource(Res.string.explain_percent_of, formatPercent(percent), formatMoney(base, currency)))
        // SH-193: how many unassigned shifts an estimate covers.
        if (term.kind == TermKind.OPEN_SHIFTS && term.rate != null && term.shifts != null) {
            add(stringResource(Res.string.explain_open_shifts_count, term.shifts!!))
        }
        when {
            hours != null && term.rate != null -> add(stringResource(Res.string.explain_hours_rate, formatHintHours(hours), formatMoney(term.rate!!, currency)))
            hours != null && term.shifts != null -> add(stringResource(Res.string.explain_shifts_hours, term.shifts!!, formatHintHours(hours)))
            hours != null -> add(stringResource(Res.string.explain_hours, formatHintHours(hours)))
        }
        val days = term.days
        val ofDays = term.ofDays
        if (days != null && ofDays != null) add(stringResource(Res.string.explain_days_of, days, ofDays))
        else if (days != null) add(stringResource(Res.string.explain_days, days))
        // SH-193: nothing to estimate unassigned shifts by: where to set a rate.
        if (term.kind == TermKind.OPEN_SHIFTS_NOT_PRICED) add(stringResource(Res.string.explain_open_shifts_set_rate))
    }
    return pieces.takeIf { it.isNotEmpty() }?.joinToString(" · ")
}

private fun sideLabel(side: Side): StringResource = when (side) {
    Side.PLAN -> Res.string.explain_side_plan
    Side.ACTUAL -> Res.string.explain_side_actual
    Side.FORECAST -> Res.string.explain_side_forecast
}

/** The sheet's title. */
fun figureTitle(figure: Figure): StringResource = when (figure) {
    Figure.LABOUR_PERCENT -> Res.string.explain_figure_labour_percent
    Figure.LABOUR_PERCENT_OF_SALES -> Res.string.explain_figure_labour_percent_of_sales
    Figure.LABOUR_COST -> Res.string.explain_figure_labour_cost
    Figure.INCOME -> Res.string.explain_figure_income
    Figure.REVENUE -> Res.string.explain_figure_revenue
    Figure.NET_SALES -> Res.string.explain_figure_net_sales
    Figure.SALES_TARGET -> Res.string.explain_figure_sales_target
    Figure.TAKINGS -> Res.string.explain_figure_takings
    Figure.SALES_INCOME -> Res.string.explain_figure_sales_income
    Figure.FIXED_REVENUE -> Res.string.explain_figure_fixed_revenue
    Figure.RECURRING_REVENUE -> Res.string.explain_figure_recurring_revenue
    Figure.ONE_OFF_REVENUE -> Res.string.explain_figure_one_off_revenue
    Figure.COSTS -> Res.string.explain_figure_costs
    Figure.OTHER_COSTS -> Res.string.explain_figure_other_costs
    Figure.FIXED_COSTS -> Res.string.explain_figure_fixed_costs
    Figure.ONE_OFF_COSTS -> Res.string.explain_figure_one_off_costs
    Figure.SHARED_COSTS -> Res.string.explain_figure_shared_costs
    Figure.RESULT -> Res.string.explain_figure_result
    Figure.MARGIN -> Res.string.explain_figure_margin
    Figure.GROSS -> Res.string.explain_figure_gross
    Figure.TAKE_HOME -> Res.string.explain_figure_take_home
    Figure.EMPLOYER_COST -> Res.string.explain_figure_employer_cost
    Figure.SHIFTS_EMPLOYER_COST -> Res.string.explain_figure_shifts_employer_cost
    Figure.MONTH_ITEMS -> Res.string.explain_figure_month_items
    Figure.BUDGET_USED -> Res.string.explain_figure_budget_used
    Figure.SHIFT_COST -> Res.string.explain_figure_shift_cost
    Figure.STAFFING_LABOUR_PERCENT -> Res.string.explain_figure_staffing_labour_percent
    Figure.PAY_CHANGE -> Res.string.explain_figure_pay_change
    Figure.SPMH -> Res.string.explain_figure_spmh
}

/** The formula in words. */
private fun formulaOf(figure: Figure): StringResource? = when (figure) {
    Figure.LABOUR_PERCENT -> Res.string.explain_formula_labour_percent
    Figure.LABOUR_PERCENT_OF_SALES -> Res.string.explain_formula_labour_percent_of_sales
    Figure.LABOUR_COST -> Res.string.explain_formula_labour_cost
    Figure.INCOME -> Res.string.explain_formula_income
    Figure.REVENUE -> Res.string.explain_formula_revenue
    Figure.NET_SALES -> Res.string.explain_formula_net_sales
    Figure.SALES_TARGET -> Res.string.explain_formula_sales_target
    Figure.TAKINGS -> Res.string.explain_formula_takings
    Figure.SALES_INCOME -> Res.string.explain_formula_sales_income
    Figure.FIXED_REVENUE -> Res.string.explain_formula_fixed_revenue
    Figure.RECURRING_REVENUE -> Res.string.explain_formula_recurring_revenue
    Figure.ONE_OFF_REVENUE -> Res.string.explain_formula_one_off_revenue
    Figure.COSTS -> Res.string.explain_formula_costs
    Figure.OTHER_COSTS -> Res.string.explain_formula_other_costs
    Figure.FIXED_COSTS -> Res.string.explain_formula_fixed_costs
    Figure.ONE_OFF_COSTS -> Res.string.explain_formula_one_off_costs
    Figure.SHARED_COSTS -> Res.string.explain_formula_shared_costs
    Figure.RESULT -> Res.string.explain_formula_result
    Figure.MARGIN -> Res.string.explain_formula_margin
    Figure.GROSS -> Res.string.explain_formula_gross
    Figure.TAKE_HOME -> Res.string.explain_formula_take_home
    Figure.EMPLOYER_COST -> Res.string.explain_formula_employer_cost
    Figure.SHIFTS_EMPLOYER_COST -> Res.string.explain_formula_shifts_employer_cost
    Figure.MONTH_ITEMS -> Res.string.explain_formula_month_items
    Figure.BUDGET_USED -> Res.string.explain_formula_budget_used
    Figure.SHIFT_COST -> Res.string.explain_formula_shift_cost
    Figure.STAFFING_LABOUR_PERCENT -> Res.string.explain_formula_staffing_labour_percent
    Figure.PAY_CHANGE -> Res.string.explain_formula_pay_change
    Figure.SPMH -> Res.string.explain_formula_spmh
}
